import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { LessonHeader, TabHeading } from '~/components/ui/ContentNav';
import {
  type ContentSearchParams,
  parseContentSearch,
  SearchFilters,
  searchQuery,
} from '~/components/ui/ContentSearch';
import { PageTitle } from '~/components/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '~/components/ui/Pagination';
import { Table } from '~/components/ui/Table';
import { NeedsNewVersion } from '~/components/ui/Versions';
import { LANGUAGE_LABELS } from '~/constants/languages';
import { CreateTopicButton } from './CreateTopicButton';

type Topic = components['schemas']['TopicAdminSummary'];
type TopicSearchItem = components['schemas']['TopicSearchItem'];

export const Route = createFileRoute('/_admin/topics/')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { lesson?: number } & ContentSearchParams => ({
    // With a lesson, its topics; without, a search across every lesson.
    lesson: s.lesson != null ? Number(s.lesson) : undefined,
    ...parseContentSearch(s),
  }),
  component: TopicsPage,
});

function TopicsPage() {
  const { lesson } = Route.useSearch();
  return lesson !== undefined ? (
    <LessonTopics lesson={lesson} />
  ) : (
    <TopicSearch />
  );
}

const columnHelper = createColumnHelper<Topic>();

const columns = [
  columnHelper.accessor('order', { header: 'Order' }),
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('title', { header: 'Title' }),
  columnHelper.accessor('status', { header: 'Status' }),
];

function LessonTopics({ lesson }: { lesson: number }) {
  const [page, setPage] = useState(0);

  // Admin endpoint: all statuses (the student lesson_topics is gated to published).
  const { data, isLoading, error } = api.adminListTopics.useQuery({
    path: { lesson_id: lesson },
    query: { limit: LIST_PAGE_SIZE, offset: LIST_PAGE_SIZE * page },
  });
  const lessonQuery = api.adminGetLesson.useQuery({
    path: { lesson_id: lesson },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <Message>Loading topics...</Message>;
  if (error) return <Message $error>Error loading topics</Message>;

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={setPage}
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      {lessonQuery.data && <LessonHeader lesson={lessonQuery.data} />}
      <TabHeading
        action={
          lessonQuery.data?.version_state === 'draft' && (
            <CreateTopicButton lessonId={lesson} />
          )
        }
      >
        Topics
      </TabHeading>
      {lessonQuery.data && lessonQuery.data.version_state !== 'draft' && (
        <NeedsNewVersion
          target={{
            courseId: lessonQuery.data.course_id,
            version: lessonQuery.data.course_version,
            item: { entity: 'lesson', lineageId: lessonQuery.data.lineage_id },
          }}
        >
          Topics can't be added, removed or reordered in a published version.
        </NeedsNewVersion>
      )}
      {pagination()}

      <Table
        table={table}
        to="/topics/$topicId"
        getParams={(row) => ({ topicId: String(row.id) })}
      />
      {pagination(true)}
    </div>
  );
}

const searchColumnHelper = createColumnHelper<TopicSearchItem>();

const searchColumns = [
  searchColumnHelper.accessor('id', { header: 'ID' }),
  searchColumnHelper.accessor('title', { header: 'Title' }),
  searchColumnHelper.accessor('lesson_title', { header: 'Lesson' }),
  searchColumnHelper.accessor('course_title', { header: 'Course' }),
  searchColumnHelper.accessor('language', {
    header: 'Language',
    cell: (info) => LANGUAGE_LABELS[info.getValue()],
  }),
  searchColumnHelper.accessor('status', { header: 'Status' }),
];

function TopicSearch() {
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const page = (params.page ?? 1) - 1;

  const { data, isLoading, error } = api.adminSearchTopics.useQuery({
    query: searchQuery(params, LIST_PAGE_SIZE),
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns: searchColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const update = (patch: ContentSearchParams, replace = true) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace });

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={(next) =>
        update({ page: next > 0 ? next + 1 : undefined }, false)
      }
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      <PageTitle>Topics</PageTitle>
      <Intro>Every topic in every lesson, drafts included.</Intro>
      <SearchFilters
        params={params}
        onChange={update}
        placeholder="Search topics by title or ID"
      />
      {isLoading ? (
        <Message>Loading topics...</Message>
      ) : error ? (
        <Message $error>Error loading topics</Message>
      ) : data?.count === 0 ? (
        <Message>No topics match.</Message>
      ) : (
        <>
          {pagination()}
          <Table
            table={table}
            to="/topics/$topicId"
            getParams={(row) => ({ topicId: String(row.id) })}
          />
          {pagination(true)}
        </>
      )}
    </div>
  );
}

const Intro = styled.p`
  color: var(--grey-700);
  font-size: 0.875rem;
  margin: 0.25rem 0 0;
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
